import { useEffect, useState } from 'react';
import {
  LINE_ITEM_TYPES,
  formatMoney,
  lineItemMoneyPeriods,
  lineItemTotal,
  parseHours,
  type Contract,
  type LineItemType,
  type Metafield,
} from '@forecastify/shared';
import { Button, Dialog, Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { MetafieldSection } from '../metafields/metafield-section';
import { DateField } from '../date-field';
import { LINE_ITEM_TYPE_KEY } from './line-item-types';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Sortétel — `2fazis.markup.html` 4343–4378. One dialog, create and edit.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **C24's inline box took a NAME AND NOTHING ELSE.** A line item's whole job is to carry an
 * estimate, and the only way to reach that field was to create the row and then find it on the
 * detail — so the natural act, *"add a line item for 40 hours"*, took two screens and a guess.
 *
 * ⚠️⚠️ **IT CREATES, AND IT NO LONGER EDITS — feedback round 4 item 7.** The mockup used one
 * dialog for both and so did this, which left every field in two places: the dialog and the detail
 * page, which is a complete inline editor for the same five things. Two editors for one row is two
 * behaviours to keep identical, and the detail is the better of them — it saves a field at a time
 * against `version`, so two people editing one line item collide on the field rather than on the
 * whole row. **The end page is the editor. This is the front door.**
 *
 * ⚠️⚠️ **AND THE TYPE IS CHOSEN HERE, WHICH IS THE OTHER HALF OF THE SAME ITEM.** It used to
 * be reachable only from the detail, so every line item was born `non_billable` — the column's
 * default — and somebody had to remember to go and change it. What a line item bills is not an
 * afterthought about it; it is the first thing anybody knows.
 *
 * ⚠️⚠️ **THE ESTIMATE IS PER PERIOD UNDER A RECURRING CONTRACT, AND THE LABEL SAYS WHICH.** The
 * mockup multiplies by the occurrence count and stores a total; `0050` stores the per-period figure
 * instead and the contract detail already says so in a footnote. The two readings of the same
 * number differ by a factor of twelve, so the label, the suffix and the hint all change with the
 * contract's type rather than leaving the reader to infer it.
 *
 * ⚠️⚠️ **U-5 · AND SO ARE THE BUDGET AND THE INCOME.** *"When adding a line item for a recurring
 * contract, the budget and invoice … should be defined per period … and a small text under it
 * shows what the total will be for the whole contract."* The note under each is the per-period
 * figure × the periods THIS line item spans (`lineItemMoneyPeriods` over the dialog's own dates,
 * clipped to the contract): a line item ending five months into a ten-month contract is × 5.
 *
 * ⚠️ **U-7 · a licence asks for its income and nothing else** — no quantity, unit or unit price.
 *
 * ⚠️ **Hours parse from `8`, `8h`, `8:00` and `8,0` through the same helper as every other hour
 * field** (CLAUDE.md §3). A second parser here would be the third one in the product.
 */
export interface LineItemDraft {
  /**
   * C30 · the workspace's `lineitem` metafields, keyed by the field's own key.
   *
   * ⚠️ Sent only when the workspace has defined fields — an empty object CLEARS every value, so an
   * absent one is what "leave them alone" looks like on the wire.
   */
  metafields: Record<string, unknown>;
  name: string;
  estimateHours: string;
  startsOn: string;
  endsOn: string;
  /**
   * ⚠️ Sent only on the pricing tier — see `pricing` on the props. ⚠️ `''` until somebody picks:
   * the billing type decides what the line item earns, and a pre-selected *Nem számlázható*
   * was a decision nobody made.
   */
  type: LineItemType | '';
  /**
   * ⚠️⚠️ **ROUND 5 ITEM 19 — and empty means *not agreed*, not zero.** A deliverable created
   * without a budget has none; the row draws an em-dash for that and `0 Ft` for a budget somebody
   * deliberately set to nothing. Strings here because they are what an input holds; the route
   * parses them exactly once, where they become the request.
   */
  budget: string;
  revenue: string;
  currencyCode: string;
}

export function LineItemDialog({
  open,
  contract,
  pricing,
  currencies,
  onClose,
  onSubmit,
  serverError,
  metafields,
  members,
}: {
  readonly open: boolean;
  /** The contract it hangs off — its type decides how the estimate reads. */
  readonly contract: Contract;
  /**
   * ⚠️ `contracts.line_item_pricing` — the TIER, not a permission, and the detail's own type
   * control is gated on the same flag (P2-1). Off the tier the field is absent and the server
   * defaults the column, which is what a workspace that cannot price line items wants.
   */
  readonly pricing: boolean;
  /**
   * ⚠️⚠️ **THE WORKSPACE'S OWN CURRENCIES — round 5 item 19, and the list is the validation.**
   * `line_items.currency_code` carries a composite foreign key to `workspace_currencies`, so a code
   * the workspace does not work in is refused by the database; offering one here would be offering
   * a refusal. Empty means the workspace priced nothing yet, and the money fields stay away.
   */
  readonly currencies: readonly { readonly code: string; readonly symbol: string }[];
  readonly onClose: () => void;
  readonly onSubmit: (draft: LineItemDraft) => void;
  readonly serverError: string | null;
  /** The workspace's fields for this entity. Empty when none are defined. */
  readonly metafields: readonly Metafield[];
  /** For the `user` metafield type. */
  readonly members: readonly { readonly id: string; readonly name: string }[];
}) {
  const t = useT();
  const [draft, setDraft] = useState<LineItemDraft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);

  const recurring = contract.type === 'recurring';

  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C55 · THE FORM IS TYPE-DRIVEN, AND THE FOUR TYPES WANT FOUR FORMS.  │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * The mockup's own predicates, transcribed rather than inferred: `liShowEst`, `liShowBudget`,
   * `liShowRevenue`, `liScopeShow`. A licence has no estimate (nobody logs against it), no budget
   * (`0151` refuses one) and no scope (it is not in or out of a retainer frame) — so it is a
   * revenue and a currency. ⚠️ The mockup's `liShowQty` row is gone with U-7.
   *
   * ⚠️ **Hiding rather than disabling.** A disabled Becslés on a licence is a control saying
   * *"you could fill this if something were different"*, and nothing would make it fillable.
   */
  const licence = draft.type === 'license';
  const showEstimate = !licence;
  const showBudget = draft.type !== 'time_and_material' && !licence;
  const showRevenue = draft.type === 'fixed' || licence;

  /**
   * U-5 · the periods THIS line item spans, from the dialog's own dates — 1 off a recurring
   * contract, where nothing is multiplied and no note is drawn.
   */
  const periods = lineItemMoneyPeriods(contract.recurrence, contract.endsOn, {
    startsOn: draft.startsOn === '' ? null : draft.startsOn,
    endsOn: draft.endsOn === '' ? null : draft.endsOn,
  });
  const symbol = currencies.find((one) => one.code === draft.currencyCode)?.symbol ?? '';
  /** *"× 5 időszak = 2 500 000 Ft"* under a per-period figure; nothing until it parses. */
  const totalNote = (typed: string): string | undefined => {
    if (!recurring || typed.trim() === '') return undefined;
    const perPeriod = Number(typed.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(perPeriod)) return undefined;
    return t('lineItem.money.totalOver', {
      count: periods,
      total: formatMoney(lineItemTotal(perPeriod, periods), {
        code: draft.currencyCode,
        symbol,
      }).trim(),
    });
  };

  useEffect(() => {
    if (!open) return;
    setError(null);
    /* 21 Oct 2026 · an internal project takes non-billable only, so it is chosen already. */
    setDraft(contract.projectInternal ? { ...emptyDraft(), type: 'non_billable' } : emptyDraft());
  }, [open, contract.projectInternal]);

  const patch = (part: Partial<LineItemDraft>): void => {
    setDraft((current) => ({ ...current, ...part }));
    setError(null);
  };

  const submit = (): void => {
    if (draft.name.trim().length === 0) {
      setError(t('contract.error.name'));
      return;
    }
    /* ⚠️ Empty is allowed and means zero — a line item can exist before anybody has estimated it.
       Anything else that will not parse is refused rather than quietly becoming zero. */
    if (draft.estimateHours.trim() !== '' && parseHours(draft.estimateHours) === null) {
      setError(t('lineItem.error.estimate'));
      return;
    }
    if (draft.startsOn !== '' && draft.endsOn !== '' && draft.endsOn < draft.startsOn) {
      setError(t('contract.error.dates'));
      return;
    }
    if (pricing && draft.type === '') {
      setError(t('lineItem.error.type'));
      return;
    }
    onSubmit(draft);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('lineItem.dialog.new')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('contract.field.name')}>
          <TextInput
            value={draft.name}
            onChange={(event) => {
              patch({ name: event.target.value });
            }}
            placeholder={t('lineItem.field.namePlaceholder')}
          />
        </Field>

        {pricing ? (
          <Field label={t('contract.column.type')} note={t('lineItem.field.typeHint')}>
            <Select
              value={draft.type}
              onChange={(event) => {
                patch({ type: event.target.value as LineItemType | '' });
              }}
              options={[
                { value: '', label: t('common.pleaseChoose') },
                /* 21 Oct 2026 · an internal project earns nothing: non-billable is its only type. */
                ...(contract.projectInternal ? (['non_billable'] as const) : LINE_ITEM_TYPES).map(
                  (type) => ({
                    value: type,
                    label: t(LINE_ITEM_TYPE_KEY[type]),
                  }),
                ),
              ]}
            />
          </Field>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('contract.column.startsOn')}>
            <DateField
              label={t('contract.column.startsOn')}
              value={draft.startsOn}
              onChange={(startsOn) => {
                patch({ startsOn });
              }}
            />
          </Field>

          <Field label={t('contract.column.endsOn')} hint={t('contract.openEnded')}>
            <DateField
              label={t('contract.column.endsOn')}
              value={draft.endsOn}
              onChange={(endsOn) => {
                patch({ endsOn });
              }}
            />
          </Field>
        </div>

        {!showEstimate ? null : (
          <Field
            label={
              recurring ? t('lineItem.field.estimatePerPeriod') : t('contract.column.estimate')
            }
            /* ⚠️ Round 5 item 18 struck the non-recurring note — *"A sortétel teljes becsült
             óraszáma"* under a field labelled *Becslés*. The RECURRING one says something the label
             does not (per period, not in total), so it stays. */
            note={recurring ? t('lineItem.field.estimateHintRecurring') : undefined}
          >
            <span className="flex items-center gap-2">
              <TextInput
                value={draft.estimateHours}
                onChange={(event) => {
                  patch({ estimateHours: event.target.value });
                }}
                placeholder="10"
                className="w-[120px]"
              />
              <span className="text-muted-foreground text-[11.5px]">
                {recurring ? t('lineItem.field.hoursPerPeriod') : t('common.hours')}
              </span>
            </span>
          </Field>
        )}

        {/**
         * ┌──────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ BÜDZSÉ, BEVÉTEL AND A CURRENCY — round 5 item 19.                    │
         * └──────────────────────────────────────────────────────────────────┘
         *
         * *"Ez hiányzik új lineitem felvételekor."* The figures could only be set afterwards, from
         * the line item's own money card — so making a priced deliverable took two screens.
         *
         * ⚠️⚠️ **ON THE PRICING TIER ONLY**, like the type field above it. A workspace without
         * pricing has no budget to agree and no currency to agree it in; drawing the fields would
         * offer something the rest of the product would then never show.
         *
         * ⚠️ **Empty is *not agreed*, and the route sends nothing rather than a zero.**
         */}
        {pricing && currencies.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-3">
            {!showBudget ? null : (
              <Field
                label={recurring ? t('lineItem.money.budgetPerPeriod') : t('lineItem.money.budget')}
                note={totalNote(draft.budget)}
              >
                <TextInput
                  value={draft.budget}
                  onChange={(event) => {
                    patch({ budget: event.target.value });
                  }}
                  placeholder="0"
                />
              </Field>
            )}
            {!showRevenue ? null : (
              <Field
                label={
                  recurring ? t('lineItem.money.revenuePerPeriod') : t('lineItem.money.revenue')
                }
                note={totalNote(draft.revenue)}
              >
                <TextInput
                  value={draft.revenue}
                  onChange={(event) => {
                    patch({ revenue: event.target.value });
                  }}
                  placeholder="0"
                />
              </Field>
            )}
            <Field label={t('lineItem.money.currency')}>
              <Select
                value={draft.currencyCode}
                onChange={(event) => {
                  patch({ currencyCode: event.target.value });
                }}
                /* ⚠️ The workspace's OWN currencies — the column's composite foreign key refuses
                   any other, so offering one would be offering a refusal. */
                options={[
                  { value: '', label: t('common.choose') },
                  ...currencies.map((one) => ({ value: one.code, label: one.code })),
                ]}
              />
            </Field>
          </div>
        ) : null}

        {/* ⚠️ Its own box, below the product's own fields — a workspace's additions come after
            what the product itself defines. Renders nothing when none are defined. */}
        <MetafieldSection
          fields={metafields}
          values={draft.metafields}
          members={members}
          onChange={(values) => {
            patch({ metafields: values });
          }}
        />

        {error === null && serverError === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error ?? serverError}
          </div>
        )}

        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit}>
            {t('contract.lineItem.add')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function emptyDraft(): LineItemDraft {
  /* ⚠️ No billing type until somebody chooses one. Off the pricing tier the field is absent, the
     route sends nothing, and the column's own default (`non_billable`) applies. */
  return {
    name: '',
    estimateHours: '',
    startsOn: '',
    endsOn: '',
    metafields: {},
    type: '',
    /* ⚠️ Round 5 item 19 — empty is *not agreed*; the route sends nothing for an empty field
       rather than a zero, and the column stays null. */
    budget: '',
    revenue: '',
    currencyCode: '',
  };
}
